/**
 * Integer seed control — draft while typing; commit via tick/Enter/dice only.
 * Cross restores persisted value without remesh.
 */

"use client";

import { CheckIcon, DicesIcon, XIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { cn } from "@repo/ui";
import { Button } from "../button";
import { Label } from "../label";

export interface SeedFieldProps {
	id?: string;
	name?: string;
	label: string;
	description?: string;
	value: number;
	min?: number;
	max?: number;
	disabled?: boolean;
	/** Commit remesh — not called while typing. */
	onCommit: (seed: number) => void;
	className?: string;
	commitAriaLabel?: string;
	cancelAriaLabel?: string;
	randomizeAriaLabel?: string;
}

function clampSeed(n: number, min: number, max: number): number {
	if (!Number.isFinite(n)) {
		return min;
	}
	return Math.min(max, Math.max(min, Math.floor(n)));
}

export function SeedField({
	id: idProp,
	name,
	label,
	description,
	value,
	min = 1,
	max = 9999,
	disabled = false,
	onCommit,
	className,
	commitAriaLabel = "Commit seed",
	cancelAriaLabel = "Cancel seed edit",
	randomizeAriaLabel = "Randomize seed",
}: SeedFieldProps) {
	const autoId = useId();
	const id = idProp ?? autoId;
	const persisted = clampSeed(value, min, max);
	const [draft, setDraft] = useState(String(persisted));
	const [focused, setFocused] = useState(false);

	useEffect(() => {
		if (!focused) {
			setDraft(String(persisted));
		}
	}, [persisted, focused]);

	const parsed = Number.parseInt(draft.trim(), 10);
	const draftValid = Number.isFinite(parsed);
	const draftClamped = draftValid ? clampSeed(parsed, min, max) : null;
	const dirty =
		draftClamped != null
			? draftClamped !== persisted
			: draft !== String(persisted);

	function commit(next: number) {
		const seed = clampSeed(next, min, max);
		setDraft(String(seed));
		setFocused(false);
		if (seed !== persisted) {
			onCommit(seed);
		}
	}

	function cancel() {
		setDraft(String(persisted));
		setFocused(false);
	}

	function randomize() {
		const span = Math.max(0, max - min);
		const seed =
			span === 0 ? min : min + Math.floor(Math.random() * (span + 1));
		setDraft(String(seed));
		setFocused(false);
		onCommit(seed);
	}

	return (
		<div
			className={cn("flex flex-col gap-1", className)}
			data-testid="seed-field"
			data-dirty={dirty ? "true" : "false"}
		>
			<Label htmlFor={id} className="text-xs font-medium">
				{label}
			</Label>
			{description ? (
				<p className="text-[11px] leading-snug text-muted-foreground">
					{description}
				</p>
			) : null}
			<div className="relative flex items-center">
				<input
					id={id}
					name={name ?? "seed"}
					type="text"
					inputMode="numeric"
					autoComplete="off"
					disabled={disabled}
					value={draft}
					aria-invalid={dirty && !draftValid ? true : undefined}
					className={cn(
						"h-9 w-full rounded-md border border-input bg-background py-1.5 pl-2.5 text-sm tabular-nums shadow-sm",
						"pr-[6.5rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
						disabled && "cursor-not-allowed opacity-50",
					)}
					data-testid="seed-field-input"
					onFocus={() => setFocused(true)}
					onChange={(e) => {
						setDraft(e.target.value);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							if (draftClamped != null) {
								commit(draftClamped);
							}
						} else if (e.key === "Escape") {
							e.preventDefault();
							cancel();
						}
					}}
				/>
				<div className="absolute inset-y-0 right-0.5 flex items-center gap-0.5">
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="size-7"
						disabled={disabled}
						aria-label={randomizeAriaLabel}
						data-testid="seed-field-randomize"
						onClick={() => randomize()}
					>
						<DicesIcon className="size-3.5" aria-hidden />
					</Button>
					{dirty ? (
						<>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-7 text-primary"
								disabled={disabled || draftClamped == null}
								aria-label={commitAriaLabel}
								data-testid="seed-field-commit"
								onClick={() => {
									if (draftClamped != null) {
										commit(draftClamped);
									}
								}}
							>
								<CheckIcon className="size-3.5" aria-hidden />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="size-7"
								disabled={disabled}
								aria-label={cancelAriaLabel}
								data-testid="seed-field-cancel"
								onClick={() => cancel()}
							>
								<XIcon className="size-3.5" aria-hidden />
							</Button>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
}
